import React, { ReactElement, ReactNode, useEffect } from 'react';
import { SubjectDTO, WorkDTO } from '../../../../api/core';
import { IntlShape, useIntl } from 'react-intl';
import { ValidationResult } from '../../../../util/validation/interfaces';
import { CheckPermissionFunction } from '../../../../Auth/components/PermissionGate';
import { Panel } from '../layout/Panel';
import { getDefaultSubject } from '../../../../Settings/emptyModels';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { InputGroup } from '../layout/InputGroup';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { SelectItem } from 'primereact/selectitem';
import { EnrichedValues, EntityType } from '../../../../util/types';

interface Props {
  checkPermission: CheckPermissionFunction;
  subjects: SubjectDTO[];
  onChange: (subject: SubjectDTO[]) => void;
  validation?: ValidationResult;
  title: string;
  typeSubset?: 'generic' | 'patent';
  type: EntityType;
  autoFocus: boolean;
  enrichedValues?: EnrichedValues;
}

export function Subjects(props: Props): ReactNode {
  return (
    <Panel title={props.title} toggleable={true}>
      <MultiInputControls<SubjectDTO>
        canEdit={props.checkPermission().canWrite}
        lines={props.subjects}
        onChange={props.onChange}
        addNew={(rows: SubjectDTO[]): number => rows.push(getDefaultSubject())}
        renderLines={(subject: SubjectDTO, onLineChange: (subjectDTO: SubjectDTO) => void, actions: Actions, idx: number): ReactElement => {
          const isNew = (
            props.enrichedValues?.workSubjects as SubjectDTO[] | undefined
          )?.some(
            (enrichedSubject: SubjectDTO): boolean =>
              enrichedSubject.subjectId === subject.subjectId &&
              enrichedSubject.type === subject.type
          ) ?? false;

          return <Subject subject={subject} idx={idx} actions={actions} onLineChange={onLineChange}
            autoFocus={props.autoFocus}
            type={props.type}
            typeSubset={props.typeSubset}
            checkPermission={props.checkPermission} validation={props.validation} title={props.title} isNew={isNew} />
        }}
      />
    </Panel>);
}

interface RowProps {
  subject: SubjectDTO;
  idx: number;
  actions: Actions;
  onLineChange: (subject: SubjectDTO) => void;
  checkPermission: CheckPermissionFunction;
  validation?: ValidationResult;
  title: string;
  typeSubset?: 'generic' | 'patent';
  type: EntityType;
  autoFocus: boolean;
  isNew?: boolean;
}

export const Subject = (props: RowProps): ReactElement => {
  const intl: IntlShape = useIntl();
  const [options, setOptions] = React.useState<SelectItem[]>([]);
  const typeSubset: 'generic' | 'patent' = props.typeSubset || 'generic';

  useEffect((): void => {
    props.onLineChange({...props.subject});
    if (props.subject.type) {
      setOptions(getVocabularyOptions(intl.locale, `${props.subject.type}_subject_list`).map((item: SelectItem): SelectItem => ({...item, label: `${item.label} (${item.value})`})));
    } else {
      setOptions([]);
    }
  }, [props.subject.type]);

  useEffect((): void => {
    if(!options.some((option: SelectItem): boolean => option.value === props.subject.subjectId)) {
      props.subject.subjectId = null;
    }
  }, [options]);

  let selectItems: SelectItem[] = [];
  switch (props.type) {
    case 'work':
      selectItems = getVocabularyOptions(intl.locale, 'subject_type_list', typeSubset);
      break;
    case 'organisation':
    case 'project':
    case 'person':
      selectItems = getVocabularyOptions(intl.locale, 'unit_subject_type_list', typeSubset);
      break;
    default:
      break;
  }

  return <InputGroup
    delete={{
      action: props.actions.delete,
      disabled: !props.checkPermission().canWrite,
    }}
  >
    <LabelledDropdown
      id={`subjects-${props.idx}-type`}
      autoFocus={props.autoFocus}
      value={props.subject.type}
      className={'w-15rem' + `${props.isNew ? ' border-primary-500 border-3' : ''}`}
      filter={true}
      permissions={props.checkPermission('type')}
      validation={props.validation?.children[props.idx]?.fields?.type}
      options={selectItems}
      onChange={(type: string): void => props.onLineChange({...props.subject, type})}
      label={`${props.title}.attr.type`}
      isNew={props.isNew}
    />
    <LabelledDropdown
      id={`subjects-${props.idx}-id`}
      value={props.subject.subjectId}
      className={'w-15rem' + `${props.isNew ? ' border-primary-500 border-3' : ''}`}
      filter={true}
      permissions={props.checkPermission('subject_id')}
      validation={props.validation?.children[props.idx]?.fields?.subject_id}
      options={options}
      disabled={options.length === 0}
      onChange={(subjectId: string): void => props.onLineChange({...props.subject, subjectId})}
      label={`${props.title}.attr.subjectId`}
      isNew={props.isNew}
    />
  </InputGroup>;
};
